import { cn } from "@/shared/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
import {
  DELIVERABLES,
  DELIVERABLE_CONFIG,
  countCompleted,
  type DeliverableKey,
} from "@/features/properties/services/deliveryStatusConfig";

/** Single-letter badge per deliverable. */
const LETTER: Record<DeliverableKey, string> = {
  survey_completed: "S",
  report_completed: "R",
  vr_tours_completed: "V",
  files_documents_completed: "F",
};

/**
 * Per-property deliverables as four labeled badges (S/R/V/F) in fixed order,
 * emerald when complete. Each badge has a tooltip naming the deliverable and its
 * status; the completed count lives in the group's accessible label.
 */
export function DeliverablesProgress({ property }: { property: Record<string, unknown> }) {
  const done = countCompleted(property);

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="flex gap-1"
        role="group"
        aria-label={`${done} of ${DELIVERABLES.length} deliverables complete`}
      >
        {DELIVERABLES.map((key) => {
          const isDone = property[key] === true;
          const status = isDone ? "Complete" : "Pending";
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <span
                  data-testid="deliverable-segment"
                  data-done={isDone}
                  aria-label={`${DELIVERABLE_CONFIG[key].label} ${isDone ? "complete" : "pending"}`}
                  className={cn(
                    "flex h-[18px] w-[18px] items-center justify-center rounded-[5px] text-[10px] font-bold",
                    isDone ? "bg-emerald-500/20 text-emerald-400" : "bg-muted text-muted-foreground/50",
                  )}
                >
                  {LETTER[key]}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <span className="font-medium">{DELIVERABLE_CONFIG[key].label}</span>
                <span className="text-muted-foreground"> · {status}</span>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
